import React, { useState, useContext, useRef, useEffect } from "react";
import { ChatContext } from "../context/ChatContext";

const ChatInput = ({ input, setInput }) => {
  const { sendMessage, loading } = useContext(ChatContext);
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !loading) {
      sendMessage(input);
      setInput("");
      // Refocus after sending
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    adjustTextareaHeight();
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "56px";
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 200; // Maximum height in pixels
      const newHeight = Math.min(scrollHeight, maxHeight);
      textarea.style.height = newHeight + "px";

      // Add or remove overflow based on content height
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.overflowY = "hidden";
      }
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  // Reset height when input is cleared
  useEffect(() => {
    if (!input) {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "56px";
        textarea.style.overflowY = "hidden";
      }
    }
  }, [input]);

  // Autofocus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-end space-x-3">
        <div className="flex-grow relative">
          <textarea
            ref={textareaRef}
            className="w-full p-4 pr-12 rounded-xl bg-gray-800/80 text-white 
                       placeholder-gray-400 focus:outline-none border border-blue-500/30 
                       focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 
                       transition-all duration-300 resize-none scrollbar-thin scrollbar-thumb-blue-500/50"
            placeholder="Ask BSA Assist..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
            autoFocus
            rows="1"
            style={{
              minHeight: "56px",
              overflowY: "hidden",
              lineHeight: "1.5",
            }}
          />

          {input.length > 0 && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-500">
              {input.length}/2000
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !input.trim()}
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 
                     transform hover:scale-110 active:scale-95 flex-shrink-0 self-end mb-1
                     ${
                       loading || !input.trim()
                         ? "bg-gray-700 text-gray-400 cursor-not-allowed opacity-50"
                         : "bg-gradient-to-r from-blue-500 to-purple-600 text-white " +
                           "hover:brightness-125 shadow-lg hover:shadow-[0_0_30px_rgba(56,189,248,0.5)]"
                     }`}
          title={loading ? "Processing..." : "Send message"}
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </button>
      </div>

      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
        <span>Press Enter to send, Shift+Enter for new line</span>
      </div>
    </form>
  );
};

export default ChatInput;
